import React, { useState, useMemo } from 'react';
import { Modal, Typography, List as AntList, Segmented, Tag, Card, Row, Col, Collapse } from 'antd';
import { UnorderedListOutlined, BarChartOutlined, AppstoreOutlined } from '@ant-design/icons';
import ParameterVisualization from '../ParameterVisualization';
import BiologicalAgeBreakdownChart from './BiologicalAgeBreakdownChart';

const { Title, Text, Paragraph } = Typography;

/**
 * Shared Parameter Modal Component
 * Used by both PatientHealthOverview and ParameterSummary for consistent display
 */
const ParameterModal = ({
  visible,
  onCancel,
  title,
  parametersData,
  priorityThresholds = {},
  sfrScores = null,
  biologicalAgeBreakdownData = null,
  biologicalAgeInfo = null
}) => {
  const [viewMode, setViewMode] = useState('priority'); // 'priority', 'category', or 'systems'

  const { flatData, groupedData } = useMemo(() => ({
    flatData: parametersData?.flat || [],
    groupedData: parametersData?.grouped || {},
  }), [parametersData]);

  // Function to categorize PhenoAge parameters by physiological systems
  // Based on Levine et al. 2018 PhenoAge research
  const categorizePhenoAgeParameters = (parameters) => {
    const systemCategories = {
      'Metabolic System': [
        'labs_protein_albumin',    // Albumin - protein metabolism, nutritional status
        'labs_glucose_glucose',    // Glucose - glucose metabolism
        'labs_hepatology_alp'      // Alkaline Phosphatase - liver metabolism
      ],
      'Renal/Kidney System': [
        'labs_renal_creatinine'    // Creatinine - kidney function
      ],
      'Inflammation System': [
        'labs_inflammation_hscrp'  // C-Reactive Protein - systemic inflammation
      ],
      'Hematologic/Immune System': [
        'labs_cbc_lymph_pct',      // Lymphocyte Percentage - immune function
        'labs_cbc_mcv',            // Mean Cell Volume - red blood cell characteristics
        'labs_cbc_rdw',            // Red Cell Distribution Width - red blood cell variation
        'labs_cbc_wbc'             // White Blood Cell Count - immune response
      ]
    };

    const categorizedParams = {};

    // Initialize categories
    Object.keys(systemCategories).forEach(category => {
      categorizedParams[category] = [];
    });

    // Categorize parameters
    parameters.forEach(param => {
      let categorized = false;

      Object.entries(systemCategories).forEach(([category, paramKeys]) => {
        if (paramKeys.includes(param.key)) {
          categorizedParams[category].push(param);
          categorized = true;
        }
      });

      // If not categorized, put in a general category
      if (!categorized) {
        if (!categorizedParams['Other Parameters']) {
          categorizedParams['Other Parameters'] = [];
        }
        categorizedParams['Other Parameters'].push(param);
      }
    });

    // Remove empty categories
    Object.keys(categorizedParams).forEach(category => {
      if (categorizedParams[category].length === 0) {
        delete categorizedParams[category];
      }
    });

    return categorizedParams;
  };

  // Priority color mapping
  const priorityColors = {
    High: '#ff4d4f',
    Moderate: '#fa8c16',
    Low: '#1890ff',
    Normal: '#52c41a',
    'No Data': '#d9d9d9'
  };

  // Render parameter list item (New Compact Design)
  const renderParameterItem = (item) => {
    const hasData = item.hasData !== false && item.zScore !== null && item.zScore !== undefined;
    const priorityColor = priorityColors[item.priority] || priorityColors['No Data'];

    return (
      <AntList.Item
        key={item.key}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px 12px',
          borderBottom: '1px solid #f0f0f0',
          background: '#fff',
        }}
      >
        {/* Left Section: Parameter Name & Category */}
        <div style={{ flex: '1.5 1 0', minWidth: '200px', paddingRight: '16px' }}>
          <Text strong style={{ display: 'block', fontSize: '14px' }}>
            {item.name || item.parameterName || item.key}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {item.subcategory || item.category || 'Unknown'}
          </Text>
        </div>

        {/* Center Section: Visualization */}
        <div style={{ flex: '3 1 0', minWidth: '250px' }}>
          {hasData ? (
            <ParameterVisualization
              zScore={item.zScore}
              currentValue={item.currentValue}
              idealTarget={item.idealTarget}
              unit={item.unit}
              normalRange={item.normalRange}
              sd={item.sd?.value}
            />
          ) : (
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary" italic>No data available</Text>
            </div>
          )}
        </div>

        {/* Right Section: Priority Score & Level */}
        <div style={{ flex: '1 1 0', minWidth: '120px', textAlign: 'right', paddingLeft: '16px' }}>
          <Text strong style={{ fontSize: '16px', display: 'block', color: priorityColor }}>
            {hasData ? item.specificImpact?.toFixed(2) : 'N/A'}
          </Text>
          <Tag color={hasData ? priorityColor : 'default'} style={{ fontWeight: 'bold' }}>
            {item.priority || 'No Data'}
          </Tag>
        </div>
      </AntList.Item>
    );
  };

  const renderContent = () => {
    if (viewMode === 'systems') {
      return <BiologicalAgeBreakdownChart data={biologicalAgeBreakdownData} />;
    }

    if (viewMode === 'category') {
      // For biological age modals, use physiological system categorization
      const isBiologicalAgeModal = title?.toLowerCase().includes('biological age');
      let categoriesToDisplay;

      if (isBiologicalAgeModal && flatData.length > 0) {
        categoriesToDisplay = categorizePhenoAgeParameters(flatData);
      } else {
        categoriesToDisplay = groupedData;
      }

      const categories = Object.keys(categoriesToDisplay);
      if (categories.length === 0) {
        return <Paragraph>No parameters found for this category.</Paragraph>;
      }

      return (
        <Collapse accordion>
          {categories.map(category => (
            <Collapse.Panel header={`${category} (${categoriesToDisplay[category].length})`} key={category}>
              <AntList
                itemLayout="vertical"
                dataSource={categoriesToDisplay[category]}
                renderItem={renderParameterItem}
              />
            </Collapse.Panel>
          ))}
        </Collapse>
      );
    }

    if (viewMode === 'priority') {
      if (flatData.length === 0) {
        return <Paragraph>No parameters found for this category.</Paragraph>;
      }
      return (
        <AntList
          itemLayout="vertical"
          dataSource={flatData}
          renderItem={renderParameterItem}
        />
      );
    }

    return null;
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width="90vw"
      style={{ top: 20 }}
      styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
    >
      {/* Biological Age Summary (only show for biological age modals) */}
      {biologicalAgeInfo && (
        <Card style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={8}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: biologicalAgeInfo.ageColor || '#666' }}>
                  {typeof biologicalAgeInfo.adjustedBiologicalAge === 'number'
                    ? biologicalAgeInfo.adjustedBiologicalAge.toFixed(2)
                    : 'N/A'}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Biological Age</div>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                  {typeof biologicalAgeInfo.chronologicalAge === 'number'
                    ? biologicalAgeInfo.chronologicalAge.toFixed(2)
                    : 'N/A'}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Chronological Age</div>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: biologicalAgeInfo.ageColor || '#666' }}>
                  {typeof biologicalAgeInfo.adjustedAgeDifference === 'number'
                    ? `${biologicalAgeInfo.adjustedAgeDifference > 0 ? '+' : ''}${biologicalAgeInfo.adjustedAgeDifference.toFixed(2)}`
                    : 'N/A'}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Adjusted Age Difference</div>
              </div>
            </Col>
          </Row>

          {/* Additional row for baseline PhenoAge difference */}
          <Row gutter={[16, 8]} style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
            <Col xs={24} sm={12}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#8c8c8c' }}>
                  {typeof biologicalAgeInfo.baselinePhenoAge === 'number'
                    ? biologicalAgeInfo.baselinePhenoAge.toFixed(2)
                    : 'N/A'}
                </div>
                <div style={{ fontSize: '11px', color: '#666' }}>Baseline PhenoAge</div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#8c8c8c' }}>
                  {typeof biologicalAgeInfo.baselineAgeDifference === 'number'
                    ? `${biologicalAgeInfo.baselineAgeDifference > 0 ? '+' : ''}${biologicalAgeInfo.baselineAgeDifference.toFixed(2)}`
                    : 'N/A'}
                </div>
                <div style={{ fontSize: '11px', color: '#666' }}>Baseline Age Difference</div>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>
          Contributing Factors:
        </Title>
        <Segmented
          options={[
            { label: 'Priority View', value: 'priority', icon: <UnorderedListOutlined /> },
            { label: 'Category View', value: 'category', icon: <AppstoreOutlined /> },
            ...(biologicalAgeBreakdownData ? [{ label: 'Systems View', value: 'systems', icon: <BarChartOutlined /> }] : [])
          ]}
          value={viewMode}
          onChange={setViewMode}
        />
      </div>



      {renderContent()}
    </Modal>
  );
};

export default ParameterModal;
